// Contract for the AI Employee setup wizard (spec §2.3-2.6 server shapes, plus the client draft
// and the step contract). Kept free of runtime imports so `node --test` can load modules that
// import it with a `.ts` suffix.
import type { ReactNode } from 'react';
import type { AgreementMeta } from '@/components/legal/AgreementAcceptanceDialog';
import type { CalendarStatus } from '../components/calendar-settings';
import type { ReplyMode, ReplyStatus } from '../components/reply-modes';

export type { AgreementMeta, CalendarStatus, ReplyMode, ReplyStatus };

// ---------------------------------------------------------------------------------------------
// Steps

export const SETUP_STEPS = ['website', 'style', 'handles', 'meet', 'golive'] as const;
export type SetupStep = (typeof SETUP_STEPS)[number];

/** Progress-bar labels, in step order. */
export const SETUP_STEP_LABELS: Record<SetupStep, string> = {
  website: 'Website',
  style: 'Style',
  handles: 'Handles',
  meet: 'Meet',
  golive: 'Go live',
};

export type SetupStatus = 'in_progress' | 'completed' | 'skipped';

export const TONE_PRESETS = ['friendly', 'casual', 'formal'] as const;
export type TonePreset = (typeof TONE_PRESETS)[number];

export const REPLY_STYLES = ['short', 'detailed', 'sales'] as const;
export type ReplyStyle = (typeof REPLY_STYLES)[number];

export const SETUP_JOBS = ['answer', 'leads', 'booking', 'after_hours', 'handoff', 'missed_calls'] as const;
export type SetupJob = (typeof SETUP_JOBS)[number];

/** `ai.setup` on BusinessSettings (spec §2.1). Dates arrive as ISO strings. */
export interface SetupRecord {
  version: 2;
  status: SetupStatus;
  step: SetupStep;
  runId?: string;
  website?: string;
  jobs?: string[];
  ruleChips?: string[];
  startedAt?: string;
  completedAt?: string;
}

/** The owner's standing "Always do, or never say" rules (`ai.behaviorRules`). */
export interface BehaviorRule {
  id: string;
  text: string;
  origin?: 'setup';
  setupKey?: string;
}

// ---------------------------------------------------------------------------------------------
// Learn run (spec §2.3)

export type StageId = 'read' | 'services' | 'policies' | 'train';
export type StageState = 'pending' | 'running' | 'done' | 'failed';
export interface RunStage {
  id: StageId;
  /** 'Reading pages', 'Learning services & prices', 'Learning policies & processes', 'Training your AI Employee'. */
  label: string;
  state: StageState;
  detail?: string;
}

/** Absolute URLs on the business's own site. */
export interface RunBrand {
  favicon?: string;
  image?: string;
  themeColor?: string;
  siteName?: string;
}

export type PageKind =
  | 'home'
  | 'services'
  | 'pricing'
  | 'contact'
  | 'about'
  | 'faq'
  | 'policy'
  | 'booking'
  | 'offers'
  | 'other';
export type PageStatus = 'reading' | 'read' | 'failed' | 'indexed';
export interface RunPage {
  url: string;
  path: string;
  title: string;
  kind: PageKind;
  status: PageStatus;
}

export interface SourceRef {
  url: string;
  quote: string;
}

export type FactKind =
  | 'business'
  | 'type'
  | 'summary'
  | 'service'
  | 'offer'
  | 'hours'
  | 'location'
  | 'phone'
  | 'email'
  | 'policy'
  | 'booking'
  | 'store'
  | 'topic';
export interface RunFact {
  id: string;
  kind: FactKind;
  label: string;
  value: string;
  detail?: string;
  source?: SourceRef;
  /** Prices and offers: the AI reads these live from the site, so they are never frozen into notes. */
  live?: boolean;
}

export type QuestionKind =
  | 'service'
  | 'policy'
  | 'hours'
  | 'contact'
  | 'area'
  | 'offer'
  | 'price'
  | 'booking'
  | 'other';
export interface RunQuestion {
  id: string;
  q: string;
  a: string;
  kind: QuestionKind;
  source?: SourceRef;
}

export type RunStatus = 'running' | 'done' | 'partial' | 'failed';

export interface RunIndexing {
  total: number;
  indexed: number;
  failed: number;
}

export interface RunFailure {
  reason: string;
  message: string;
  /** Present when the site blocked the reader, for the owner's web person to allow-list. */
  crawlerIp?: string;
}

/**
 * The background read of the rest of a website, after the quick setup read (deep-read-spec). On
 * the run view and on each website from GET knowledge/:b/websites.
 */
export type DeepReadStatus = 'off' | 'queued' | 'reading' | 'done';
export interface DeepReadView {
  /** 'done': nothing queued or running, and the cap is reached or no unread page is left. */
  status: DeepReadStatus;
  /** Pages chosen to read so far, up to `cap` (key pages count toward it). Grows as links are found. */
  total: number;
  /** Of those, indexed and searchable. */
  read: number;
  /** Of those, failed (including pages a render would have needed once the render budget ran out). */
  failed: number;
  cap: number;
}

/**
 * A business detail the website states differently from the account's settings (or that the
 * settings lack). The AI answers from the settings, so the owner should know.
 */
export type ConflictKind = 'hours' | 'address' | 'phone';
export interface RunConflict {
  id: string;
  kind: ConflictKind;
  /** What the website says. */
  site: string;
  /** What the account's settings say; null when not set. */
  account: string | null;
  note?: string;
}

export interface RunView {
  runId: string;
  status: RunStatus;
  host: string | null;
  startedAt: string;
  elapsedMs: number;
  /** Bumped on every server write. The client ignores a response older than what it has. */
  seq: number;
  stages: RunStage[];
  brand: RunBrand;
  pages: RunPage[];
  facts: RunFact[];
  questions: RunQuestion[];
  /** 4-6 suggestions specific to this business. */
  ruleChips: RuleChip[];
  /** 2-4 sentence overview built from the facts. */
  summary: string | null;
  indexing: RunIndexing;
  failure?: RunFailure;
  /** e.g. "Trained on northsidehvac.example in 12.4 seconds. It now knows …" */
  doneText?: string;
  /**
   * True once the run will change no more, indexing included. Indexing can stop short of every
   * page (a time limit or an error), so this, not the counts, ends the wait.
   */
  finished?: boolean;
  /**
   * The background read of the rest of this run's website, computed on every read (so it moves
   * without `seq`). Absent before it starts, without a website, and from older servers.
   */
  deepRead?: DeepReadView;
  /** Details the website and the account's settings disagree on. Absent from older servers. */
  conflicts?: RunConflict[];
}

export interface LearnStartRequest {
  url?: string;
  noWebsite?: boolean;
}
export interface LearnStartResponse {
  runId: string;
  status: 'running';
}

// ---------------------------------------------------------------------------------------------
// Rule chips and selections (spec §2.5)

export type RuleChipKind = 'behavior' | 'handoff' | 'lead_goal' | 'booking_goal';
export interface RuleChip {
  id: string;
  label: string;
  kind: RuleChipKind;
  recommended?: boolean;
  /** kind behavior → ai.behaviorRules */
  behavior?: { text: string };
  /** kind handoff → topicRules action 'escalate' */
  topic?: { label: string; description: string; examples: string[] };
}

export interface SelectionsRequest {
  step: SetupStep;
  assistantName?: string;
  tonePreset?: TonePreset;
  replyStyle?: ReplyStyle;
  /** The chips the owner has switched ON (full objects, so custom chips work). */
  ruleChips?: RuleChip[];
  jobs?: SetupJob[];
  /** Confirmed business summary → BusinessAiProfile.overview (only if empty or written by setup). */
  summary?: string;
  /** Fact cards the owner removed (kept on the run so they're not reused). */
  removedFactIds?: string[];
}

/** What the setup created so far, for the Go live recap. */
export interface SetupCreated {
  topics: string[];
  goals: string[];
  rules: string[];
  notes: number;
  overview: boolean;
}

export interface SelectionsResponse {
  settings: AiSettingsResponse;
  created: SetupCreated;
}

// ---------------------------------------------------------------------------------------------
// Setup state (GET setup/:b/state)

export interface SetupBusiness {
  name: string;
  type: string | null;
  phone: string | null;
  timezone: string | null;
  hoursSet: boolean;
}

export interface SetupStateSettings {
  assistantName: string | null;
  /** null when the stored tone is custom text. */
  tonePreset: TonePreset | null;
  replyStyle: ReplyStyle | null;
  behaviorRules: BehaviorRule[];
}

export interface SetupStateResponse {
  setup: SetupRecord | null;
  latestRun: RunView | null;
  suggestedUrl: string | null;
  business: SetupBusiness;
  settings: SetupStateSettings;
}

// ---------------------------------------------------------------------------------------------
// Style preview (spec §2.4)

export interface StylePreviewRequest {
  question: string;
  baseReply: string;
  sources?: string[];
  assistantName?: string;
  businessName?: string;
  rules: Array<{ id: string; label: string }>;
}
export type StyleVariants = Record<TonePreset, Record<ReplyStyle, string>>;
export interface RuleEffect {
  kind: 'append' | 'replace' | 'example';
  text?: string;
  example?: { customer: string; reply: string };
}
export interface StylePreviewResponse {
  variants: StyleVariants;
  ruleEffects: Record<string, RuleEffect>;
  costUsd: number;
}

// ---------------------------------------------------------------------------------------------
// AI settings (GET/PUT /api/ai-employee/settings/:b). Only the fields the wizard reads or writes
// are typed; the rest pass through.

export type AiChannel = 'sms' | 'webchat' | 'facebook' | 'instagram' | 'email' | 'googleBusinessMessage';

export interface SetupTopicRule {
  _id?: string;
  label: string;
  description: string;
  examples?: string[];
  action: 'respond' | 'escalate' | 'silent_escalate' | 'ignore';
  customResponse?: string;
  enabled?: boolean;
  origin?: 'setup';
  setupKey?: string;
}

export interface SetupObjective {
  _id?: string;
  name: string;
  goal: string;
  trigger?: string;
  enabled?: boolean;
  steps: Array<{ key: string; prompt: string; choices?: string[] }>;
  onComplete?: { action: 'none' | 'escalate' | 'silent_escalate'; label?: string; message?: string };
  origin?: 'setup';
  setupKey?: string;
}

export interface SetupAiSettings {
  enabled?: boolean;
  mode?: string;
  operatingHoursMode?: string;
  openHoursMode?: ReplyMode;
  closedHoursMode?: ReplyMode;
  delayMinutes?: number;
  tone?: string;
  customInstructions?: string;
  channelSettings?: Partial<Record<AiChannel, boolean>>;
  topicRules?: SetupTopicRule[];
  objectives?: SetupObjective[];
  notifyOnAiResponse?: boolean;
  defaultAction?: string;
  assistantName?: string;
  tonePreset?: TonePreset | null;
  replyStyle?: ReplyStyle;
  behaviorRules?: BehaviorRule[];
  setup?: SetupRecord;
  [field: string]: unknown;
}

export interface AiSettingsResponse {
  ai: SetupAiSettings;
  replyStatus?: ReplyStatus;
}

/** PUT body. Arrays (topicRules, objectives) and channelSettings replace what is stored. */
export type AiSettingsUpdate = Partial<
  Pick<
    SetupAiSettings,
    | 'openHoursMode'
    | 'closedHoursMode'
    | 'delayMinutes'
    | 'tone'
    | 'customInstructions'
    | 'channelSettings'
    | 'topicRules'
    | 'objectives'
    | 'notifyOnAiResponse'
    | 'assistantName'
    | 'tonePreset'
    | 'replyStyle'
    | 'behaviorRules'
  >
> & { setup?: Partial<SetupRecord> };

// ---------------------------------------------------------------------------------------------
// Knowledge (notes, FAQs, documents, websites)

export interface KnowledgeNoteInput {
  title: string;
  content?: string;
  tags?: string[];
  visibility?: 'customer' | 'internal';
}
export interface KnowledgeNote {
  _id: string;
  title: string;
  content: string;
  tags: string[];
  visibility: 'customer' | 'internal';
  status: 'active' | 'archived';
  createdAt: string;
  updatedAt: string;
  vectorStatus?: string;
}

export interface FaqInput {
  question: string;
  answer?: string;
}
export interface FaqCreateResponse {
  created: Array<{ _id: string; question: string; answer?: string; status: 'active' | 'draft' }>;
}

export type UploadSourceType = 'pdf' | 'docx' | 'csv' | 'text';
export interface UploadRequest {
  /** Without the extension. */
  filename: string;
  sourceType: UploadSourceType;
  /** Base64 for pdf/docx, plain text otherwise. */
  content: string;
  description?: string;
  visibility?: 'customer' | 'internal';
}
export interface UploadResponse {
  id: string;
  status: 'uploaded';
  message?: string;
}

export type DocumentStatus = 'uploaded' | 'extracting' | 'chunking' | 'embedding' | 'indexed' | 'failed';
export interface KnowledgeDocument {
  id: string;
  filename: string;
  description?: string;
  sourceType: string;
  status: DocumentStatus | (string & {});
  chunkCount: number;
  visibility: string;
  createdAt: string;
  error?: string;
}
export interface KnowledgeListResponse {
  count: number;
  documents: KnowledgeDocument[];
}

export interface WebsiteCreateResponse {
  id: string;
  domain: string;
  rootUrl: string;
  rootPageId: string;
}

// ---------------------------------------------------------------------------------------------
// Rep endpoints outside the AI service

export interface WidgetKeyResponse {
  apikey: string | null;
  acceptableWebsites: string[] | null;
  hasMultiLocation: boolean;
}

export interface MissedCallMessageResponse {
  missedCallMessage: string;
  alwaysSendMissedCallMessage: boolean;
}

export interface CalendarConnectOptions {
  accountSelection?: 'reviews' | 'another';
  /** Where Google sends the owner back. The server allows only these two. */
  returnPath?: '/ai/start' | '/ai/integrations/google-calendar';
}

// ---------------------------------------------------------------------------------------------
// Client draft (what the owner has picked in this run; mirrored to sessionStorage)

/** 'off' is offered only to an AI that already stays quiet while open (after-hours replies only). */
export type OpenHoursChoice = ReplyMode;

export interface SetupAddition {
  kind: 'note' | 'faq' | 'document' | 'website';
  id?: string;
  label: string;
  at: string;
}

export interface SetupDraft {
  website: {
    /** What the owner typed (or the suggested URL). */
    url: string;
    noWebsite: boolean;
    /** Fact cards removed on the "what it learned" grid. */
    removedFactIds: string[];
    /** Edited summary. null means "use run.summary as is". */
    summary: string | null;
    /**
     * Settings conflicts the owner said are the website's fault ("My website is out of date").
     * Only hides them here; nothing is sent.
     */
    dismissedConflictIds: string[];
  };
  assistantName: string;
  /** null keeps a custom tone the owner wrote on the Personality page. */
  tonePreset: TonePreset | null;
  replyStyle: ReplyStyle;
  /**
   * Chips switched on, as full objects. null until the owner touches a chip: the run's
   * `recommended` chips (or the ones saved on the server) apply. Use `setup.chips` for the
   * resolved lists.
   */
  ruleChips: RuleChip[] | null;
  /** "Write your own" chips, kept so they stay listed when switched off. */
  customChips: RuleChip[];
  jobs: SetupJob[];
  goLive: {
    openHoursMode: OpenHoursChoice;
    delayMinutes: number;
    /** null until the owner flips it; Go live defaults it from the widget key / number. */
    webchat: boolean | null;
    sms: boolean | null;
  };
  /** Knowledge added during this run, for the Go live recap. */
  added: SetupAddition[];
}

/** Background selection saves: 'retrying' is a save sent again after one failed. */
export type SaveState = 'idle' | 'saving' | 'failed' | 'retrying';

/** A step change whose selections are still to be saved (mirrored, so a reload sends it). */
export interface PendingSave {
  from: SetupStep;
  to: SetupStep;
  leaving?: boolean;
}

export type RunPhase =
  /** No run yet. */
  | 'idle'
  /** POST accepted, first poll not back yet. */
  | 'starting'
  /** Reading pages and extracting facts. */
  | 'reading'
  /** Facts are complete; pages are still being indexed. */
  | 'indexing'
  | 'done'
  | 'failed';

// ---------------------------------------------------------------------------------------------
// Controller exposed to steps (use-setup.ts) and the step contract

export interface SetupController {
  businessId: string;
  /** False until setup state is available (from the server, or this tab's saved copy). */
  ready: boolean;
  /** Set when the first load failed and nothing was cached. */
  loadError: string | null;
  reload: () => Promise<void>;
  state: SetupStateResponse | null;

  /** The latest learn run, kept fresh by polling. */
  run: RunView | null;
  runPhase: RunPhase;
  /** Set when polling keeps failing or the run expired; polling backs off but continues. */
  pollError: string | null;
  /** Starts (or rejoins) a learn run. Rejects with AiApiError (400 reason, 409/429 quota). */
  startLearn: (input: LearnStartRequest) => Promise<string>;
  /** Forgets the current run locally, e.g. "Try another address". */
  clearRun: () => void;
  /** Reads the run once more, e.g. after the owner changed their business details in another tab. */
  refreshRun: () => Promise<void>;

  draft: SetupDraft;
  updateDraft: (patch: Partial<SetupDraft> | ((draft: SetupDraft) => Partial<SetupDraft>)) => void;
  /** Rule chips resolved against the run and the server: every chip to show, and those on. */
  chips: { available: RuleChip[]; selected: RuleChip[] };

  step: SetupStep;
  /** Furthest step reached; progress segments up to here can be jumped to. */
  maxStep: SetupStep;

  /** PUT selections. Updates `aiSettings`/`state.settings` and fires `ai-settings-changed`. */
  saveSelections: (body: SelectionsRequest) => Promise<SelectionsResponse>;
  /**
   * Queues a save of what `from` collected with `step: to`. Saves go one at a time, in order; the
   * body is built when it is sent, from the latest draft, and saves waiting together go as one
   * (later values win). Resolves once this one is saved (or there was nothing new to save) and
   * rejects when its save failed; it stays queued for a retry either way. The shell calls it and
   * moves on without waiting. `leaving`: Exit, which saves nothing when there is no setup record
   * and nothing was collected.
   */
  saveStep: (from: SetupStep, to: SetupStep, options?: { leaving?: boolean }) => Promise<void>;
  /**
   * Waits for every queued save, sending again any that failed. Rejects if one still fails. For
   * what depends on the choices being saved: the Meet chat's sends, Go live, the recap.
   */
  flushSaves: () => Promise<void>;
  /** Sends failed saves again (the bottom bar's "Try again"). */
  retrySaves: () => void;
  /** 'failed' until a retry, or the next step change, saves it. */
  saveState: SaveState;
  /** Why the last save failed, while `saveState` is 'failed'. */
  saveError: string | null;
  /** A save is on its way. */
  saving: boolean;
  /** From the last selections save. */
  created: SetupCreated | null;

  /** GET /api/ai-employee/settings, loaded with the state. */
  aiSettings: AiSettingsResponse | null;
  refreshAiSettings: () => Promise<AiSettingsResponse | null>;
  /** The AI replies in at least one period. */
  isLive: boolean;

  /** After Go live succeeds: sets the completed flag, clears progress, fires ai-settings-changed. */
  completeSetup: () => void;
  /** Adds a recap row for knowledge created in this run. */
  recordAddition: (addition: Omit<SetupAddition, 'at'>) => void;
  /** Polite screen-reader announcement. */
  announce: (message: string) => void;

  businessName: string;
  /** The learned site's host, if any. */
  host: string | null;
  /** The draft name, or "Wren". */
  assistantName: string;
  brand: RunBrand;
  /** Owner or location admin: may connect a calendar, accept agreements, change hours. */
  canManage: boolean;
}

export interface FooterLink {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

/** What a step tells the bottom bar. Register it with `useStepFooter` (use-setup.ts). */
export interface StepFooter {
  /** Primary CTA label. Defaults to the step's "Next: …" label. */
  label?: string;
  /** Default true. */
  canContinue?: boolean;
  /** Spinner on the CTA; Next, Back and the progress bar are ignored while busy. */
  busy?: boolean;
  /**
   * Hide the bottom-bar CTA (Enter still runs onContinue while canContinue). 'desktop' hides it
   * from the sm breakpoint up only, for a step with its own CTA there.
   */
  hidden?: boolean | 'desktop';
  /** Hide Back, and lock the progress bar (e.g. while going live, and once live). */
  hideBack?: boolean;
  /**
   * Default true. False: Enter and → don't run onContinue, for an action that shouldn't be one
   * stray key press away (the step's own focused button still works).
   */
  keyboard?: boolean;
  /**
   * Runs when the owner presses the CTA, Enter or →. Return false (or throw) to stay. When it
   * resolves, the shell moves to the next step and saves this step's selections behind it. On
   * the last step there is no next step, so this is the whole action.
   */
  onContinue?: () => unknown;
  /** Extra left-side links (after "Skip setup" or "Back"). */
  links?: FooterLink[];
}

/** Props every step component receives. */
export interface StepProps {
  businessId: string;
  step: SetupStep;
  /** Same object as `useSetup()`. `state` is always loaded when a step renders. */
  setup: SetupController & { state: SetupStateResponse };
  goNext: () => void;
  goBack: () => void;
  goTo: (step: SetupStep) => void;
  /** 1 when this step was entered going forward, -1 going back. */
  direction: 1 | -1;
}

export type StepComponent = (props: StepProps) => ReactNode;
